import { isWeb } from "@memorang/helpers/src/common";
import React from "react";
import { Animated, StyleSheet, View } from "react-native";
import { useTheme } from "react-native-paper";

type ProgressBarProps = {
	progress: number;
	size?: number;
	strokeWidth?: number;
};

type ProgressBarIndeterminateProps = {
	size?: number;
	strokeWidth?: number;
};

const DETERMINATE_ANIMATION_DURATION = 1000;
const INDETERMINATE_ANIMATION_DURATION = 2000;

function BaseProgressBar({ progress }: ProgressBarProps) {
	const { colors } = useTheme();
	const { current: timer } = React.useRef<Animated.Value>(
		new Animated.Value(0),
	);

	React.useEffect(() => {
		Animated.timing(timer, {
			toValue: progress,
			duration: DETERMINATE_ANIMATION_DURATION,
			useNativeDriver: false,
		}).start();
	}, [progress]);

	return (
		<View style={styles.container}>
			<Animated.View
				style={{
					backgroundColor: colors.primary,
					width: timer.interpolate({
						inputRange: [0, 1],
						outputRange: ["0%", "100%"],
					}),
					borderRadius: 4,
				}}
			/>
			<Animated.View
				style={{
					backgroundColor: colors.secondaryContainer,
					borderRadius: 4,
					flex: 1,
				}}
			/>
		</View>
	);
}

const ProgressBarIndeterminate = (_props: ProgressBarIndeterminateProps) => {
	const { colors } = useTheme();
	const { current: timer } = React.useRef<Animated.Value>(
		new Animated.Value(0),
	);

	const indeterminateAnimation =
		React.useRef<Animated.CompositeAnimation | null>(null);

	React.useEffect(() => {
		if (!indeterminateAnimation.current) {
			indeterminateAnimation.current = Animated.timing(timer, {
				toValue: 1,
				duration: INDETERMINATE_ANIMATION_DURATION,
				useNativeDriver: !isWeb,
			});
		}

		Animated.loop(indeterminateAnimation.current).start();
	}, []);

	return (
		<View style={styles.container}>
			<Animated.View
				style={{
					backgroundColor: colors.secondaryContainer,
					borderRadius: 4,
					width: timer.interpolate({
						inputRange: [0, 1],
						outputRange: ["0%", "100%"],
					}),
				}}
			/>
			<Animated.View
				style={{
					backgroundColor: colors.primary,
					width: timer.interpolate({
						inputRange: [0, 0.3, 0.85, 1],
						outputRange: ["0%", "50%", "15%", "0%"],
					}),
					borderRadius: 4,
				}}
			/>
			<Animated.View
				style={{
					backgroundColor: colors.secondaryContainer,
					borderRadius: 4,
					width: timer.interpolate({
						inputRange: [0, 1],
						outputRange: ["100%", "0%"],
					}),
				}}
			/>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		height: 4,
		borderRadius: 4,
		overflow: "hidden",
		flexDirection: "row",
		width: "100%",
		gap: 0,
	},
});

export const ProgressBar = Object.assign(BaseProgressBar, {
	Indeterminate: ProgressBarIndeterminate,
}) as React.FC<ProgressBarProps> & {
	Indeterminate: React.FC<ProgressBarIndeterminateProps>;
};
